"use client";

import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import type { WisWizAPI } from "@/services/api";
import { WisWizAPIError } from "@/services/api";
import { lifetimePurchaseUrl } from "@/lib/lifetimeCheckout";
import {
  leesBetaling,
  terugNaarCheckoutUrl,
  wisBetaling
} from "@/lib/betaalRecord";

/* wiswiz #156 — terug in de app (of op de site) zonder resultaatpagina na een Mollie-hop.

   Wat er op 3 sep gebeurde: de Android-app (Capacitor) opent de Mollie-pagina in de systeembrowser; de
   leerling kwam 5–8 minuten later terug in een app die Android intussen had herstart, op het home, zonder
   één woord over de betaling. Drie keer. Deze haak kijkt op elk moment van terugkomst — koude start, app
   weer actief (Capacitor appStateChange), tab weer zichtbaar, terugknop uit de cache (pageshow) — of er een
   betaling van net loopt (record van vóór de hop, zie betaalRecord.ts) en vraagt de backend naar precies die
   betaling:
     betaald        → de resultaatpagina (/payment/result), die anders nooit kwam;
     niet betaald   → de checkout van de hop, met één regel "De betaling is niet afgerond. Probeer het opnieuw."
   De betaalpagina's zelf (/payment/…) blijven erbuiten: die tonen hun eigen uitkomst en wissen dan het record.
   Een deep link die nét landt (wiswiz://payment/result) krijgt eerst even de tijd; daarom de korte wachttijd. */

type Aanleiding = "start" | "app-actief" | "zichtbaar" | "terugknop";

const DEEPLINK_WACHT_MS = 2500;

export default function useBetalingTerug(api: WisWizAPI) {
  const router = useRouter();
  const bezigRef = useRef(false);

  useEffect(() => {
    let actief = true;

    const controleer = async (via: Aanleiding) => {
      if (bezigRef.current) return;
      if (!leesBetaling()) return;
      bezigRef.current = true;
      try {
        await new Promise((r) => setTimeout(r, DEEPLINK_WACHT_MS));
        if (!actief) return;
        if (window.location.pathname.startsWith("/payment/")) return;
        const rec = leesBetaling();
        if (!rec) return;
        if (rec.purchase_id && rec.subscription_id) {
          await api.getLifetimePurchase(rec.subscription_id);
          if (!actief) return;
          wisBetaling();
          router.replace(lifetimePurchaseUrl(rec.subscription_id));
          return;
        }
        if (!rec.id) {
          /* oudere backend zonder payment_id: niets te vragen, niets aannemen */
          wisBetaling();
          return;
        }
        const res = await api.getPaymentStatus(rec.id);
        if (!actief) return;
        wisBetaling();
        try {
          posthog.capture("checkout_terug_uit_mollie", {
            via,
            status: res.status,
            platform: Capacitor.getPlatform(),
            plan_id: rec.plan_id
          });
        } catch {
          // meten mag nooit blokkeren
        }
        if (res.status === "paid") {
          router.replace(`/payment/result?id=${encodeURIComponent(rec.id)}`);
        } else {
          router.replace(terugNaarCheckoutUrl(rec));
        }
      } catch (err) {
        /* 401: nog niet ingelogd — het record blijft, de volgende ingelogde terugkomst vraagt het opnieuw.
           Andere fouten (netwerk): niets aannemen, niets tonen. */
        if (err instanceof WisWizAPIError && err.statusCode === 404) {
          wisBetaling();
        }
      } finally {
        bezigRef.current = false;
      }
    };

    const opZichtbaar = () => {
      if (document.visibilityState === "visible") void controleer("zichtbaar");
    };
    const opPageshow = (e: PageTransitionEvent) => {
      if (e.persisted) void controleer("terugknop");
    };
    document.addEventListener("visibilitychange", opZichtbaar);
    window.addEventListener("pageshow", opPageshow);

    let luisteraar: Promise<{ remove: () => Promise<void> }> | null = null;
    if (Capacitor.isNativePlatform()) {
      luisteraar = App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) void controleer("app-actief");
      });
    }

    void controleer("start");

    return () => {
      actief = false;
      document.removeEventListener("visibilitychange", opZichtbaar);
      window.removeEventListener("pageshow", opPageshow);
      luisteraar?.then((l) => l.remove()).catch(() => {});
    };
  }, [api, router]);
}
